import { PhFileX, PhSealCheck, PhWarning } from 'ember-phosphor-icons';
import type { IntlService } from 'ember-intl';
import type { Store } from '@warp-drive/core';
import { query } from '@warp-drive/utilities/json-api';
import {
  ABORTED_STATUSES,
  SUCCEEDED_STATUSES,
} from 'tio-data/data/resources/eligibility-file-job-log.constants';
import type { EligibilityFileJobLog } from 'tio-data/data/resources/eligibility-file-job-log.type';
import { oncePerVisit } from 'tio-employee/utils/partner-dashboard/memo';
import type { AttentionItem, AttentionSource } from 'tio-employee/utils/partner-dashboard/registry';

/**
 * Where a run stands, from the partner's point of view. `awaitingYou` is the
 * only state the partner can act on; `awaitingReview` waits on Tuition.io.
 */
export type EligibilityRunPhase =
  'awaitingYou' | 'awaitingReview' | 'failed' | 'aborted' | 'completed' | 'inProgress';

// FAILED_HEADER_VALIDATION is served by tio-api but missing from the shared status list.
const FAILED_STATUSES = ['ERROR_REPORTED', 'FAILED_VALIDATION', 'FAILED_HEADER_VALIDATION'];

/** Maps a job-log status to its phase; unknown statuses read as still in progress. */
export function eligibilityRunPhase(status: string | undefined): EligibilityRunPhase {
  if (status === 'REQUIRES_USER_APPROVAL') return 'awaitingYou';
  if (status === 'REQUIRES_REVIEWER_APPROVAL') return 'awaitingReview';
  if (status && FAILED_STATUSES.includes(status)) return 'failed';
  if ((ABORTED_STATUSES as readonly string[]).includes(status ?? '')) return 'aborted';
  if ((SUCCEEDED_STATUSES as readonly string[]).includes(status ?? '')) return 'completed';
  return 'inProgress';
}

/** Employee changes in a run. */
export interface RunCounts {
  created: number;
  updated: number;
  deactivated: number;
  reactivated: number;
}

/** The latest eligibility file run, reduced to what the dashboard shows. */
export interface EligibilityRun {
  id: string;
  fileName: string;
  phase: EligibilityRunPhase;
  /** ISO timestamp the run started. */
  startedAt?: string;
  /**
   * Final counts once the run completed; while it waits on approval, the
   * preview of what it would change; otherwise the changes a run that stopped
   * part-way already applied. Null when none of those exist.
   */
  counts: RunCounts | null;
  /** True when `counts` is the pre-approval preview rather than changes actually applied. */
  isPreview: boolean;
  /** Rows the run reported as errors. */
  errors: number;
}

function toRun(log: EligibilityFileJobLog): EligibilityRun {
  const phase = eligibilityRunPhase(log.status);
  // tio-api seeds `counts` with zeros, so before completion they only mean something once non-zero:
  // a run that stopped part-way still reports the changes it applied.
  const applied =
    log.counts &&
    (['created', 'updated', 'deactivated', 'reactivated'] as const).some((k) => log.counts[k])
      ? log.counts
      : null;
  // A preview only describes a file waiting on approval; after that it is stale next to applied counts.
  const awaitingApproval = phase === 'awaitingYou' || phase === 'awaitingReview';
  const preview = awaitingApproval ? (log.previewCounts ?? null) : null;
  const source = phase === 'completed' ? log.counts : (preview ?? applied);
  return {
    id: String(log.id),
    fileName: log.fileName ?? '',
    phase,
    startedAt: log.startTime || undefined,
    counts: source
      ? {
          created: source.created ?? 0,
          updated: source.updated ?? 0,
          deactivated: source.deactivated ?? 0,
          reactivated: source.reactivated ?? 0,
        }
      : null,
    isPreview: Boolean(preview),
    errors: Array.isArray(log.jobErrors) ? log.jobErrors.length : 0,
  };
}

/**
 * The company's most recent eligibility file run, or null when it has none.
 * Shared per visit by the card and the attention source.
 */
export const loadLatestEligibilityRun = oncePerVisit(
  async (ctx): Promise<EligibilityRun | null> => {
    if (!ctx.companyId) return null;
    const store = ctx.owner.lookup('service:store') as Store;
    const { content } = await store.request<{ data: EligibilityFileJobLog[] }>(
      query<EligibilityFileJobLog>('eligibility-file-job-log', {
        'filter[company-id]': ctx.companyId,
        sort: '-start-time',
        'page[limit]': 1,
      })
    );
    const latest = content.data[0];
    return latest ? toRun(latest) : null;
  }
);

/**
 * A Needs Your Attention row when the latest eligibility file is waiting on
 * the partner's approval, failed, or completed with row errors. Runs waiting
 * on Tuition.io, aborted or still processing need nothing from the partner.
 */
export const ELIGIBILITY_FILE_SOURCE: AttentionSource = {
  id: 'eligibility-file',
  drivenBy: ['accountOwner', 'partnerAdmin'],
  load: async (ctx) => {
    const run = await loadLatestEligibilityRun(ctx);
    if (!run) return [];
    const intl = ctx.owner.lookup('service:intl') as IntlService;
    const show = {
      label: intl.t('partner_dashboard.eligibility.view_action'),
      route: 'authenticated.admin.eligibility-files.show',
      model: run.id,
    };
    // No `dueAt`: an upload time is not a deadline, so these rows sort after dated ones.
    const base = { id: `eligibility-file:${run.id}` };

    if (run.phase === 'awaitingYou') {
      const item: AttentionItem = {
        ...base,
        title: intl.t('partner_dashboard.eligibility.awaiting_title'),
        description: run.counts
          ? intl.t('partner_dashboard.eligibility.awaiting_description', { ...run.counts })
          : intl.t('partner_dashboard.eligibility.awaiting_description_no_preview'),
        icon: PhSealCheck,
        urgency: 'urgent',
        dueLabel: intl.t('partner_dashboard.eligibility.awaiting_label'),
        action: { ...show, label: intl.t('partner_dashboard.eligibility.review_action') },
      };
      return [item];
    }
    if (run.phase === 'failed') {
      return [
        {
          ...base,
          title: intl.t('partner_dashboard.eligibility.failed_title'),
          description: intl.t('partner_dashboard.eligibility.failed_description', {
            file: run.fileName,
          }),
          icon: PhFileX,
          urgency: 'urgent',
          dueLabel: intl.t('partner_dashboard.eligibility.failed_label'),
          action: show,
        },
      ];
    }
    if (run.phase === 'completed' && run.errors) {
      return [
        {
          ...base,
          title: intl.t('partner_dashboard.eligibility.errors_title', { count: run.errors }),
          description: intl.t('partner_dashboard.eligibility.errors_description'),
          icon: PhWarning,
          urgency: 'soon',
          action: show,
        },
      ];
    }
    return [];
  },
};
